import React, { useCallback, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import Button, { Color } from "../../../../../../../../components/Form/Button";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import LoadingModal from "../../../../../../../../components/LoadingModal";
import { useAppState } from "../../../../../../../../context/StateContext";
import useActionAutomaticSequence from "../../../../../../../../hooks/useActionAutomaticSequence";
import { AutomaticSequencing } from "../../../../../../../../models/automatic.sequencing";
import { AutomaticSequenceOptions, CompanyProfile } from "../../../../../../../../util/enums";
import UIAccordion from "../UIAccordion";
import { AutomaticOption } from "./AutomaticOption";
import { isMoving } from "Util/validation";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean
  }
}

export const AutomaticSequence = (props: Props) => {
  const {permission} = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const { sequences, createAutomaticSequence, updateAutomaticSequence, getAllSequences } =
    useActionAutomaticSequence();

  const { refetch, isFetching } = getAllSequences();
  const { mutate: createSequence, isLoading: isCreating } = createAutomaticSequence(refetch);
  const { mutate: updateSequence, isLoading: isUpdating } = updateAutomaticSequence(refetch);

  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [sequencesToUpdate, setSequencesToUpdate] = useState<AutomaticSequencing[]>([]);
  const [expanded, setExpanded] = useState<boolean>(true);

  const optionsValue = useMemo(() => {
    switch (currentUser?.company.profile.initials) {
      case CompanyProfile.FM:
        return [AutomaticSequenceOptions.SHIPMENT];
      case CompanyProfile.MV:
        return [
          AutomaticSequenceOptions.USER,
          AutomaticSequenceOptions.CUSTOMER,
          AutomaticSequenceOptions.AGENT,
          AutomaticSequenceOptions.QUOTE,
          AutomaticSequenceOptions.OPERATIONS,
          AutomaticSequenceOptions.TRANSSHIPMENT,
          AutomaticSequenceOptions.SHIPMENT,
        ];
      case CompanyProfile.SPP:
        return [AutomaticSequenceOptions.INVOICE_NUMBER];
      default:
        return [];
    }
  }, [AutomaticSequenceOptions]);

  const handleFindSequence = useCallback(
    (type: string) => {
      return sequences?.find((sequence) => sequence.type.toLowerCase() === type.toLowerCase());
    },
    [sequences]
  );

  const handleChange = (
    sequence: AutomaticSequencing,
    sequencesArr: AutomaticSequencing[],
    update: (value: AutomaticSequencing[]) => void
  ) => {
    if (sequencesArr.length > 0) {
      const index = sequencesArr.findIndex(
        (seq) => seq.type.toLowerCase() === sequence.type.toLowerCase()
      );

      if (index >= 0) {
        const updatedSequences = sequencesToUpdate;

        updatedSequences[index] = { ...updatedSequences[index], ...sequence };
        update([...updatedSequences]);
      } else {
        update([...sequencesToUpdate, sequence]);
      }
    } else {
      update([sequence]);
    }
  };

  const handleChangeSequences = (sequence: AutomaticSequencing) => {
    handleChange(sequence, sequencesToUpdate, (sequences) => {
      setSequencesToUpdate([...sequences]);
    });
  };

  const handleSubmit = () => {
    if (sequencesToUpdate.length > 0) {
      const sequencesCopy = [...sequencesToUpdate];

      for (const sequence of sequencesCopy) {
        if (sequence.id) updateSequence({ id: sequence.id, sequence });
        else createSequence({ ...sequence, companyId: currentUser?.companyId });
      }

      setSequencesToUpdate([]);
    }
  };

  return (
    <UIAccordion
      expand={expanded}
      onClick={() => setExpanded(!expanded)}
      dataCy="automaticSequencing"
      accordionTitle={translate("automaticSequencing")}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        {optionsValue.map((option) => (
          <Grid item md={12} lg={12} key={option}>
            <SkeletonComponent isLoading={isFetching}>
              <AutomaticOption
                type={option}
                sequence={handleFindSequence(option)}
                handleChange={handleChangeSequences}
                isCancelled={isCancelled}
              />
            </SkeletonComponent>
          </Grid>
        ))}

        {(
          (permission && isShowAction(currentUser, permission.edit) &&
          sequencesToUpdate?.length > 0) ||
          (!isMoving(currentUser?.company) &&
          sequencesToUpdate?.length > 0)
        ) && (
          <Grid item container md={12} lg={12} justifyContent="flex-end">
            <Button
              customColor={Color.CANCEL}
              onClick={() => {
                setSequencesToUpdate([]);
                setIsCancelled(true);
                setTimeout(() => setIsCancelled(false), 500);
              }}
            >
              {translate("cancel")}
            </Button>
            <Button
              customColor={Color.SUCCESS}
              data-cy="submitAutomaticButton"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            >
              {translate("save")}
            </Button>
          </Grid>
        )}
        <LoadingModal showing={isCreating || isUpdating} />
      </Grid>
    </UIAccordion>
  );
};
